import { useContext, useEffect, useState } from 'react';
import Styled from 'styled-components';
import { UserContext } from '../../context/UserContext';
import { Unauthorized } from '../../components/Unauthorized';
import { PERMISSIONS } from '../../constants';
import { getPermissionsMatrix } from '../../utils/API/permissionsMatrix';
import { renderSpinner } from '../../utils/Utility';

const Scroll = Styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  background: #fff;
`;

const Table = Styled.table`
  --grid-border: 1px solid #d0d7de;
  border-collapse: separate;
  border-spacing: 0;
  width: max-content;
  min-width: 100%;
  margin-bottom: 24px;
  border: var(--grid-border);

  thead th {
    position: sticky;
    top: 0;
    height: 48px;
    padding: 0 12px;
    font-size: 11px;
    line-height: 12px;
    letter-spacing: 0;
    color: var(--brand-dark);
    text-transform: uppercase;
    text-align: center;
    background: #f3f6fa;
    border-right: var(--grid-border);
    border-bottom: 2px solid var(--brand-dark);
    z-index: 2;
  }

  thead th:last-child {
    border-right: none;
  }

  thead th:first-child {
    left: 0;
    text-align: left;
    z-index: 3;
    border-right: var(--grid-border);
  }

  tbody th,
  tbody td {
    font-size: 12px;
    color: var(--text-color);
    padding: 6px 12px;
    border-right: var(--grid-border);
    border-bottom: var(--grid-border);
    text-align: center;
    white-space: nowrap;
  }

  tbody td:last-child,
  tbody th:last-child {
    border-right: none;
  }

  tbody tr:last-child th,
  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody th {
    text-align: left;
    font-weight: 500;
    position: sticky;
    left: 0;
    z-index: 1;
    background: #fff;
    border-right: var(--grid-border);
  }

  tbody tr:nth-child(even) th,
  tbody tr:nth-child(even) td {
    background: var(--gray-95);
  }

  td.granted {
    color: var(--brand-dark);
    font-weight: 700;
  }
`;

const PermissionsMatrixPage = () => {
  const { hasClaim } = useContext(UserContext);
  const [data, setData] = useState({ roles: [], permissions: [], matrix: {} });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getPermissionsMatrix()
      .then(setData)
      .finally(() => setIsLoading(false));
  }, []);

  if (!hasClaim(PERMISSIONS.PERMISSIONS_VIEW)) return <Unauthorized />;

  return (
    <>
      {renderSpinner(isLoading)}
      <div className="content__wrapper">
        <div className="headerPage content">
          <div className="title">Matrice ruoli / permessi</div>
        </div>
        <div
          className="content__fixed_height"
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: '16px',
            paddingBottom: '48px',
            minHeight: 0,
          }}
        >
          <Scroll>
            <Table>
              <thead>
                <tr>
                  <th>Permission / Role</th>
                  {data.roles.map((role) => (
                    <th key={role}>{role}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.permissions.map((perm) => (
                  <tr key={perm}>
                    <th>{perm}</th>
                    {data.roles.map((role) => {
                      const granted = (data.matrix[role] || []).includes(perm);
                      return (
                        <td
                          key={`${role}-${perm}`}
                          className={granted ? 'granted' : ''}
                        >
                          {granted ? 'x' : ''}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </Table>
          </Scroll>
        </div>
      </div>
    </>
  );
};

export default PermissionsMatrixPage;
